import React, { Fragment } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

// Components

import Form from '@lib/wrappers/Form';
import UniversalInput from '@lib/wrappers/UniversalInput';

// ActionCreators

import { changeData } from '@actions/ssap/ssapActionCreators';
import { selectIsItuQuestionsDisabled } from '@reducers/appReducer';

// Utils

import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { questionsVisibility } from '@utils/miscellaneous/pageAndQuestionsVisibility';

// Constants

import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';



/**
 *
 * @param {Object} props
 * @returns {React.Component} - 'Additional Information - Member Enrolled In Other Health Insurance
 *
 */
const Page4_66 = props => {

    const {
        data,
        environment: {
            isState
        },
        formName,
        familyStatus,
        household,
        householdMembers,
        hhmIndex : member,
        inlineCurrentIncomeEnabled,
        currentHhm,
        alwaysDisplayMedicaidQuestions
    } = props;

    const isItuQuestionsDisabled = useSelector(selectIsItuQuestionsDisabled);

    const {
        applyingForCoverageIndicator,
        personId,
        healthCoverage: {
            absentParent,
            eligibleITU,
            receivedITU,
            unpaidBill
        },
        americanIndianAlaskaNative: {
            memberOfFederallyRecognizedTribeIndicator
        },
        name,
        inlineEligibility: {
            preliminaryMedicaidStatus,
            preliminaryEmergencyMedicaidStatus,
            preliminaryChipStatus,
        },
        mcoSelection
    } = currentHhm;

    const onFieldChange = (newData) => {
        const newState = { ...data };
        newState.taxHousehold.householdMember[member] = { ...newData };
        props.actions.changeData(newState);
    };

    const onFieldChange_needChildCoverage = (...args) => {
        const [ newData, indexInArray ] = args;

        const newState = { ...data };

        newState.taxHousehold.householdMember[indexInArray] = newData;

        newState.taxHousehold.householdMember[member].healthCoverage.coveredDependentChild =
            familyStatus.relatedPersonsIds.children.every(child => {
                const {
                    healthCoverage: {
                        childHaveCurrentCoverage
                    }
                } = householdMembers[child-1];

                return childHaveCurrentCoverage === true;
            });

        props.actions.changeData(newState);
    };

    const childrenHasHealthCoverage = (familyStatus) => {
        const {
            areChildrenShown,
            filteredChildren
        } = questionsVisibility.areChildrenHasHealthCoverageShown({
            alwaysDisplayMedicaidQuestions,
            familyStatus,
            household,
            householdMembers,
            inlineCurrentIncomeEnabled,
            personId,
            preliminaryMedicaidStatus
        });

        return areChildrenShown
            ? (
                <div className = 'grid-row gi-component gi-checkbox' >
                    <fieldset className = 'usa-fieldset' >
                        <legend className = 'required-false text-normal' >
                            <FormattedMessage id = 'ssap.input.legend.children_has_health_coverage' />
                        </legend>
                        <ul className = 'usa-list usa-list--unstyled' >
                            {
                                filteredChildren.map((child) => {

                                    const currentChild = householdMembers[child-1];

                                    const {
                                        applicantGuid,
                                        name,
                                        healthCoverage: {
                                            childHaveCurrentCoverage
                                        }
                                    } = currentChild;

                                    return (
                                        <li key = { applicantGuid } >
                                            <UniversalInput
                                                label = { {
                                                    text: normalizeNameOfPerson(name)
                                                } }
                                                inputData = { {
                                                    isInputRequired: false,
                                                    type: INPUT_TYPES.CHECKBOX,
                                                    name: 'childHaveCurrentCoverage',
                                                    classes: [ '' ],
                                                    checked: childHaveCurrentCoverage,
                                                    value: childHaveCurrentCoverage,
                                                    currentData: currentChild,
                                                    fields: [ 'healthCoverage' ],
                                                    isInputStandalone: true
                                                } }
                                                inputActions = { {
                                                    onFieldChange: onFieldChange_needChildCoverage
                                                } }
                                                indexInArray = { child-1 }
                                                errorClasses = { [ 'margin-l-0' ] }
                                            />
                                        </li>
                                    );
                                })
                            }
                        </ul>
                    </fieldset>
                </div>
            )
            : null;
    };

    const params = {
        alwaysDisplayMedicaidQuestions,
        applyingForCoverageIndicator,
        familyStatus,
        memberOfFederallyRecognizedTribeIndicator,
        inlineCurrentIncomeEnabled,
        isItuQuestionsDisabled,
        isState,
        preliminaryMedicaidStatus,
        preliminaryEmergencyMedicaidStatus,
        preliminaryChipStatus
    };

    const isAbsentParentShown = questionsVisibility.isMemberWithAbsentParentShown(params);
    const isIndianHealthServicesShown = questionsVisibility.isIndianHealthServicesShown(params);
    const isMcoSelectionShown = questionsVisibility.isMcoSelectionShown(params);
    const isRetroactiveAssistanceShown = questionsVisibility.isRetroactiveAssistanceShown(params);

    const replacements = {
        personName: (
            <strong>
                { normalizeNameOfPerson(name) }
            </strong>
        )
    };

    return (
        <Fragment>
            <Form
                { ...{
                    formName
                } }
            >
                <div
                    className = 'fade-in'
                    id = 'aiStateHealthBenefit'
                >
                    <div className = 'subsection' >
                        {
                            isIndianHealthServicesShown &&

                            <Fragment>
                                <UniversalInput
                                    legend = { {
                                        legendText: 'ssap.input.legend.eligible_for_tribal_program',
                                        legendTextReplacements: replacements
                                    } }
                                    label = { {
                                        showRequiredIcon: true
                                    } }
                                    inputData = { {
                                        isInputRequired: true,
                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                        name: 'eligibleITU',
                                        value: eligibleITU,
                                        options: DROPDOWN_OPTIONS.YES_NO,
                                        validationType: VALIDATION_TYPES.CHOSEN,
                                        currentData: householdMembers[member],
                                        fields: [ 'healthCoverage' ],
                                        isInputStandalone: true
                                    } }
                                    inputActions = { {
                                        onFieldChange: onFieldChange
                                    } }
                                    errorClasses = { [ 'margin-l-0' ] }
                                />
                                <UniversalInput
                                    legend = { {
                                        legendText: 'ssap.input.legend.services_from_tribal_program',
                                        legendTextReplacements: replacements
                                    } }
                                    label = { {
                                        showRequiredIcon: true
                                    } }
                                    inputData = { {
                                        isInputRequired: true,
                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                        name: 'receivedITU',
                                        value: receivedITU,
                                        options: DROPDOWN_OPTIONS.YES_NO,
                                        validationType: VALIDATION_TYPES.CHOSEN,
                                        currentData: householdMembers[member],
                                        fields: [ 'healthCoverage' ],
                                        isInputStandalone: true
                                    } }
                                    inputActions = { {
                                        onFieldChange: onFieldChange
                                    } }
                                    errorClasses = { [ 'margin-l-0' ] }
                                />
                            </Fragment>

                        }
                        {
                            isRetroactiveAssistanceShown  &&

                            <UniversalInput
                                legend = { {
                                    legendText: 'ssap.input.legend.non_internationalized_text',
                                    legendTextReplacements: {
                                        text: (
                                            <Fragment>
                                                <p className = 'required-true' >
                                                    <FormattedMessage
                                                        id = 'ssap.input.legend.medical_bills'
                                                        values = { replacements }
                                                    />
                                                </p>
                                                <FormattedMessage id = 'ssap.input.legend.medical_bills.note' />
                                            </Fragment>
                                        )
                                    }
                                } }
                                label = { {
                                    showRequiredIcon: false
                                } }
                                inputData = { {
                                    isInputRequired: true,
                                    type: INPUT_TYPES.RADIO_BUTTONS,
                                    name: 'unpaidBill',
                                    value: unpaidBill,
                                    options: DROPDOWN_OPTIONS.YES_NO,
                                    validationType: VALIDATION_TYPES.CHOSEN,
                                    currentData: householdMembers[member],
                                    fields: [ 'healthCoverage' ],
                                    isInputStandalone: true
                                } }
                                inputActions = { {
                                    onFieldChange: onFieldChange
                                } }
                                errorClasses = { [ 'margin-l-0' ] }
                            />
                        }
                        {
                            childrenHasHealthCoverage(familyStatus)
                        }
                        {
                            isAbsentParentShown &&

                            <UniversalInput
                                legend = { {
                                    legendText: 'ssap.input.legend.parents_live_outside',
                                    legendTextReplacements: replacements
                                } }
                                label = { {
                                    showRequiredIcon: true
                                } }
                                inputData = { {
                                    isInputRequired: true,
                                    type: INPUT_TYPES.RADIO_BUTTONS,
                                    name: 'absentParent',
                                    value: absentParent,
                                    options: DROPDOWN_OPTIONS.YES_NO,
                                    validationType: VALIDATION_TYPES.CHOSEN,
                                    currentData: householdMembers[member],
                                    fields: [ 'healthCoverage' ],
                                    isInputStandalone: true
                                } }
                                inputActions = { {
                                    onFieldChange: onFieldChange
                                } }
                                errorClasses = { [ 'margin-l-0' ] }
                            />
                        }
                        {
                            isMcoSelectionShown &&

                            <UniversalInput
                                legend = { {
                                    dataInstructions: 'instructional_text.mco_selection',
                                    legendText: 'ssap.input.legend.mco_selection',
                                    legendTextReplacements: replacements
                                } }
                                label = { {
                                    showRequiredIcon: true
                                } }
                                inputData = { {
                                    isInputRequired: true,
                                    type: INPUT_TYPES.RADIO_BUTTONS,
                                    name: 'mcoSelection',
                                    value: mcoSelection,
                                    options: DROPDOWN_OPTIONS.MCO_SELECTIONS,
                                    validationType: VALIDATION_TYPES.NOT_UNSPECIFIED,
                                    currentData: householdMembers[member],
                                    fields: [ ],
                                    isInputStandalone: true
                                } }
                                inputActions = { {
                                    onFieldChange: onFieldChange
                                } }
                                errorClasses = { [ 'margin-l-0' ] }
                            />

                        }
                    </div>
                </div>
            </Form>
        </Fragment>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,

    data: state.ssap.data,

    household: state
        .ssap
        .data
        .taxHousehold,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page4_66);
